import { Form, useActionData, useLoaderData, useNavigation, useRouteLoaderData, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { DriftReport, PilotMetrics } from "@edm/contracts";
import type { MessageKey } from "../../i18n/types.js";
import { DraftRowsSkeleton, Loading } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import { isApiError } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Badge, Button, Card, FormError, HealthIcon, KeyValue, LinkButton, PageHeader, Skeleton } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

/**
 * Health: where memory is rotting. The healthy state is an empty page.
 *
 * Coverage leads, because it is not one health item among several — it is the
 * ceiling on the whole product. An unclaimed path can't be checked on a pull
 * request and Ask Agent has nothing to say about it.
 *
 * This is the bad-news page. Rates and counts that read as progress belong on
 * Pulse: one page cannot want to be full and want to be empty at the same time.
 */

type DriftItem = DriftReport["findings"][number];

const GROUPS = ["stale_decision", "orphan_constraint", "repeated_exception", "unenforced_rule"] as const;
const GROUP_TITLE = {
  stale_decision: "health.stale.title",
  orphan_constraint: "health.orphan.title",
  repeated_exception: "health.waived.title",
  unenforced_rule: "health.unenforced.title",
} as const satisfies Record<(typeof GROUPS)[number], MessageKey>;

async function fallback<T>(p: Promise<T>, value: T): Promise<T> {
  try {
    return await p;
  } catch {
    return value;
  }
}

export function healthLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  const api = context.get(apiContext).workspace(ws.id);
  const health = Promise.all([
    fallback(api.driftFindings(), { findings: [] as DriftItem[] } as DriftReport),
    fallback<PilotMetrics | null>(api.pilotMetrics(), null),
  ]).then(([drift, metrics]) => ({ findings: drift.findings ?? [], metrics }));
  return { health: deferred(health) };
}

export async function healthAction({ context, request }: ActionFunctionArgs) {
  const ws = context.get(workspaceContext);
  const api = context.get(apiContext).workspace(ws.id);
  const form = await request.formData();
  try {
    await api.resolveDrift(String(form.get("id")), form.get("outcome") === "confirm" ? "confirm" : "dismiss");
    return { ok: true };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

function Coverage({ unmapped, components, base }: { unmapped: number; components: number; base: string }) {
  const { t } = useI18n();
  // Claimed vs. unclaimed is the honest reading available from here: the engine
  // reports mapped components and the paths drift found with no owner.
  const total = components + unmapped;
  const percent = total === 0 ? 0 : Math.round((components / total) * 100);
  return (
    <Card title={t("health.coverage.title")}>
      {total === 0 ? (
        <p className="dt-muted">{t("health.coverage.none")}</p>
      ) : (
        <>
          <div className="dt-coverage">
            <span className="dt-coverage__value">{percent}%</span>
            <span className="dt-muted">
              {t("health.coverage.claimed", { percent })}
              {unmapped > 0 && <> · {t("health.coverage.unclaimed", { count: unmapped })}</>}
            </span>
          </div>
          <div className="dt-coverage__bar">
            <span className="dt-coverage__fill" style={{ width: `${percent}%` }} />
          </div>
        </>
      )}
      <p className="dt-muted">{t("health.coverage.ceiling")}</p>
      {unmapped > 0 && (
        <LinkButton to={`${base}/settings/repositories`} variant="primary">
          {t("health.coverage.action")}
        </LinkButton>
      )}
    </Card>
  );
}

/** Coverage first, then a group of drift findings with their buttons. */
function HealthBodySkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <Card title={t("health.coverage.title")}>
        <div className="dt-coverage">
          <span className="dt-coverage__value">
            <Skeleton width={72} />
          </span>
          <Skeleton width={260} />
        </div>
        <Skeleton shape="block" className="dt-coverage__bar" />
        <p>
          <Skeleton width="64%" />
        </p>
      </Card>
      <Card title={<Skeleton width={180} />} padded={false}>
        <DraftRowsSkeleton
          count={3}
          actions={
            <>
              <Skeleton shape="badge" width={88} />
              <Skeleton shape="button-sm" />
              <Skeleton shape="button-sm" width={64} />
            </>
          }
        />
      </Card>
    </Loading>
  );
}

export function HealthSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("health.title")} description={t("health.description")} />
      <HealthBodySkeleton />
    </>
  );
}

export function HealthRoute() {
  const { health } = useLoaderData<typeof healthLoader>();
  const result = useActionData() as { error?: string } | undefined;
  const { t } = useI18n();

  return (
    <>
      <PageHeader title={t("health.title")} description={t("health.description")} />
      <FormError message={result?.error} />
      <Deferred data={health} fallback={<HealthBodySkeleton />}>
        {(data) => <Health {...data} />}
      </Deferred>
    </>
  );
}

function Health({ findings, metrics }: { findings: DriftItem[]; metrics: PilotMetrics | null }) {
  const busy = useNavigation().state === "submitting";
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  const base = `/w/${workspace.slug}`;

  const unmapped = findings.filter((f) => f.kind === "unmapped_component").length;
  const grouped = GROUPS.map((kind) => ({ kind, items: findings.filter((f) => f.kind === kind) })).filter((g) => g.items.length > 0);

  return (
    <>
      <Coverage unmapped={unmapped} components={metrics?.components ?? 0} base={base} />

      {/* Precision belongs here, not on Pulse: a rising false-positive rate is bad
          news about the rules themselves, and the product's own kill signal. */}
      {metrics && (
        <Card title={t("ci.metrics.trust")} data-testid="trust">
          <KeyValue
            items={[
              {
                key: t("ci.metrics.falsePositiveRate"),
                value: metrics.falsePositiveRate < 0 ? <span className="dt-muted">{t("ci.metrics.unknown")}</span> : `${Math.round(metrics.falsePositiveRate * 100)}%`,
              },
              {
                key: t("ci.metrics.acceptedFindingRate"),
                value: metrics.acceptedFindingRate < 0 ? <span className="dt-muted">{t("ci.metrics.unknown")}</span> : `${Math.round(metrics.acceptedFindingRate * 100)}%`,
              },
            ]}
          />
          {metrics.notes?.map((note) => (
            <p key={note} className="dt-subtle">
              {note}
            </p>
          ))}
        </Card>
      )}

      {grouped.length === 0 ? (
        <EmptyState title={t("health.empty.title")} hint={t("health.empty.hint")} icon={<HealthIcon size={18} />} />
      ) : (
        grouped.map(({ kind, items }) => (
          <Card key={kind} title={t(GROUP_TITLE[kind])} padded={false}>
            <ul className="dt-drafts">
              {items.map((f) => (
                <li key={f.id ?? f.title} className="dt-draft" data-testid="drift">
                  <div className="dt-stack dt-gap-1">
                    <strong>{f.title}</strong>
                    <span className="dt-muted">{f.explanation}</span>
                    {[...(f.decisionIds ?? []), ...(f.constraintIds ?? []), ...(f.componentIds ?? [])].length > 0 && (
                      <span className="dt-subtle dt-mono">{[...(f.decisionIds ?? []), ...(f.constraintIds ?? []), ...(f.componentIds ?? [])].join(" · ")}</span>
                    )}
                  </div>
                  <span className="dt-inline dt-gap-2">
                    <Badge tone={f.confidence >= 0.8 ? "warning" : "neutral"}>{t("health.confidence", { percent: Math.round(f.confidence * 100) })}</Badge>
                    {f.id && (
                      <>
                        <Form method="post">
                          <input type="hidden" name="id" value={f.id} />
                          <input type="hidden" name="outcome" value="confirm" />
                          <Button type="submit" size="sm" variant="secondary" loading={busy}>
                            {t("ci.drift.confirm")}
                          </Button>
                        </Form>
                        <Form method="post">
                          <input type="hidden" name="id" value={f.id} />
                          <input type="hidden" name="outcome" value="dismiss" />
                          <Button type="submit" size="sm" variant="ghost" loading={busy}>
                            {t("ci.drift.dismiss")}
                          </Button>
                        </Form>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ))
      )}

      <p className="dt-subtle">{t("health.hypothesis")}</p>
    </>
  );
}
